import React from "react"
import { TableHeaderColumn, TableRow, TableHeader, TableRowColumn, TableBody } from "material-ui"
import strings from "../../../../localization/strings"
import Table from "../../../../components/common/Table"
import { IUIUser } from "../../../../models"
import * as userHelper from "../../../../helpers/userHelper"
import ExternalLink from "../../../../components/common/externalLink/ExternalLink"
import * as payGroupAndTypeHelper from "../../../../helpers/payGroupAndTypeHelper"
import * as hierarchyHelper from "../../../../helpers/hierarchyHelper"
import * as payrollHelper from "../../../../helpers/payrollHelper"
import moment from "moment"
import * as timeHelper from "../../../../helpers/timeHelper"
import ImageLoader from "../../../../components/misc/ImageLoader"
import { checkJobFieldAvailable } from "../../../../helpers/jobHelper"
import accountStore from "../../../../stores/AccountStore"

interface IProps {
    readonly: boolean
    employees: IUIUser[]
    selectedEmployees: IUIUser[]
    total: number
    offset: number
    limit: number
    limitOptions: number[]
    onPageClick(offset: number): void
    onLimitChange(limit: number): void
    handleSelectEmployees(employees: IUIUser[]): void
}

const JobEmployeesTable: React.SFC<IProps> = (props: IProps) => {
    const getPayCalendarType = (payCalendarType) => {
        switch (payCalendarType) {
            case 0:
                return strings.Weekly
            case 1:
                return strings.Bi_Weekly
            case 2:
                return strings.Monthly
            case 3:
                return strings.Semi_Monthly
        }
    }
    return (
        <Table
            readonly={props.readonly}
            items={props.employees}
            allowClickRow
            handleSelect={props.handleSelectEmployees}
            total={props.total}
            offset={props.offset}
            limit={props.limit}
            limitOptions={props.limitOptions}
            onPageClick={props.onPageClick}
            onLimitChange={props.onLimitChange}
        >
            <TableHeader displaySelectAll={!props.readonly} adjustForCheckbox={!props.readonly}>
                <TableRow>
                    <TableHeaderColumn style={{ width: 25, paddingLeft: 5, paddingRight: 5 }} />
                    <TableHeaderColumn style={styles.imageColumn} />
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Name}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Employee_ID}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Pay_Group}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Payroll_Hierarchy}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Pay_Calendar_Type}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{checkJobFieldAvailable('singular') ? `Default ${accountStore.getCurrentAccountSettings().CustomFieldSettings.JobFieldLabels.SingularJobLabel}`: strings.Default_Job}</TableHeaderColumn>
                    <TableHeaderColumn style={{ ...styles.columnValue, width: 50 }}>{strings.Active}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Previous_Pay_Period}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Pay_Period}</TableHeaderColumn>
                    <TableHeaderColumn style={styles.columnHeader}>{strings.Last_Day_Worked}</TableHeaderColumn>
                </TableRow>
            </TableHeader>
            <TableBody deselectOnClickaway={false} displayRowCheckbox={!props.readonly}>
                {props.employees.map((employee, index) => {
                    const payGroupId = payGroupAndTypeHelper.getPayGroupIdByPayrollPayGroupData(employee)
                    const payGroup = payGroupAndTypeHelper.getPayGroupById(payGroupId)
                    const thisLastPayrollPeriod = payrollHelper.getThisLastPayrollPeriod(payGroupId)
                    const previousPayPeriod = thisLastPayrollPeriod.lastPayrollPeriod
                    const thisPayPeriod = thisLastPayrollPeriod.thisPayrollPeriod
                    return (
                        <TableRow data-intercom-target={index === 0 && "menu-Jobs-JobDetails-AssignedEmployees-openEmployeeDetails"} key={index} selected={props.selectedEmployees.find(item => item.Id === employee.Id) ? true : false}>
                            <TableRowColumn data-intercom-target={index === 0 && "menu-Jobs-JobDetails-AssignedEmployees-openEmployeeDetailsInNewTab"} style={{ width: 25, paddingLeft: 5, paddingRight: 5, paddingTop: 4 }}>
                                <ExternalLink
                                    url={"/user-details/" + employee.Id + "/" + userHelper.getValidSubPages()[0]}
                                />
                            </TableRowColumn>
                            <TableRowColumn style={styles.imageColumn}>
                                {(employee.ProfileImageS3Url || employee.ProfileImageID) &&
                                    <ImageLoader url={employee.ProfileImageS3Url} fixedHeight={75} avatar src={"/users/profileimage/" + employee.ProfileImageID} key={`smallImg-${index}`} />
                                }
                            </TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{`${employee.FirstName} ${employee.LastName}`}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{employee.ExternalId}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{payGroup ? payGroup.Name : ""}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{hierarchyHelper.getHierarchyItemNameByHierarchyId(employee.PayrollHierarchyItemId)}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{payGroup ? getPayCalendarType(payGroup.PaySettings.PayCalendarType) : ""}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{employee.DefaultJobName}</TableRowColumn>
                            <TableRowColumn style={{ ...styles.columnValue, width: 50 }}>{employee.Active ? strings.Active : strings.Inactive}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{`${moment(previousPayPeriod.StartDate).format("M/DD")} - ${moment(previousPayPeriod.EndDate).format("M/DD")}`}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{`${moment(thisPayPeriod.StartDate).format("M/DD")} - ${moment(thisPayPeriod.EndDate).format("M/DD")}`}</TableRowColumn>
                            <TableRowColumn style={styles.columnValue}>{employee.LastDayWorked && moment(timeHelper.keepConvertUTCToLocal(employee.LastDayWorked)).format("MMM Do YYYY")}</TableRowColumn>
                        </TableRow>
                    )
                })}
            </TableBody>
        </Table>
    )
}

export default JobEmployeesTable

const styles = {
    columnValue: {
        textAlign: "center",
        cursor: "pointer",
        paddingLeft: 5,
        paddingRight: 5
    },
    columnHeader: {
        textAlign: "center",
        paddingLeft: 5,
        paddingRight: 5
    },
    imageColumn: {
        width: 45,
        paddingLeft: 5,
        paddingRight: 5
    }
}
